// Pages/Login.js
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { saveToken } from "../utils/Authe";
import {
  Button,
  TextField,
  Typography,
  Container,
  Box,
  Paper,
  CssBaseline,
  ThemeProvider,
  createTheme,
} from "@mui/material";
import backgroundImage from "../assets/loginBackground.jpg";
import logo from "../assets/warals-logo.png";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [clientCode, setClientCode] = useState(() => {
    return sessionStorage.getItem('clientCode') || "";
  });

  const [error, setError] = useState("");
  const navigate = useNavigate();

  const theme = createTheme({
    palette: {
      primary: { main: "#667eea" },
      secondary: { main: "#764ba2" },
      background: { default: "#ffffff" },
    },
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");

    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    if (!baseUrl) {
      console.error("❌ REACT_APP_API_BASE_URL is missing in your .env file!");
      setError("API base URL not configured. Please check your .env file.");
      return;
    }

    try {
      const response = await fetch(`${baseUrl}/student/login/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          password,
          client_code: clientCode, // ✅ Added client_code here
        }),
      });

      if (!response.ok) {
        throw new Error("Invalid email, password, or client code");
      }

      const data = await response.json();
      console.log("✅ Login successful:", data);

      const accessToken = data?.tokens?.access;
      if (accessToken) {
        // Save token, student ID, and student name to session storage
        saveToken(accessToken, "student_token");
        sessionStorage.setItem("student_id", data.student.id);
        sessionStorage.setItem("student_name", data.student.name);
        sessionStorage.setItem("student_email", data.student.email);
        //  sessionStorage.removeItem('clientCode');
        navigate("/student-dashboard");
      } else {
        throw new Error("Token not received from server");
      }
    } catch (err) {
      console.error("❌ Login error:", err);
      setError(err.message);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          zIndex: -1,
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />
      <Container
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          zIndex: 1,
        }}
      >
       <Paper
  elevation={6}
  sx={{
    padding: "40px 35px",
    minHeight: "60vh",
    width: "450px",
    display: "flex",
    justifyContent: "center",
    borderRadius: "16px",
    background: "rgba(255, 255, 255, 0.95)",
    backdropFilter: "blur(10px)",
    boxShadow: "0 8px 32px rgba(102, 126, 234, 0.2), 0 0 60px rgba(118, 75, 162, 0.15)",
    border: "1px solid rgba(102, 126, 234, 0.1)",
  }}
>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              width: "400px",
            }}
            component="form"
            onSubmit={handleLogin}
          >
            <Box sx={{ mb: 3 }}>
              <img
                src={logo}
                alt="Company Logo"
                style={{ width: "auto", height: "100px" }}
              />
            </Box>
            <Typography
              variant="h5"
              component="h1"
              gutterBottom
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                fontWeight: 700,
                mb: 3
              }}
            >
              Student Login
            </Typography>

            {/* ✅ Email Field */}
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              id="email"
              label="Username"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: '#667eea',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#667eea',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#667eea',
                },
              }}
            />

            {/* ✅ Password Field */}
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: '#667eea',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#667eea',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#667eea',
                },
              }}
            />

            {/* ✅ Client Code Field */}
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              name="clientCode"
              label="Client Code"
              id="clientCode"
              value={clientCode}
              onChange={(e) => setClientCode(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: '#667eea',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#667eea',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#667eea',
                },
              }}
            />

            {error && (
              <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                {error}
              </Typography>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{
                mt: 3,
                py: 1.5,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
                borderRadius: "8px",
                textTransform: "none",
                fontSize: "16px",
                fontWeight: 600,
                '&:hover': {
                  background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
                  boxShadow: "0 6px 20px rgba(102, 126, 234, 0.6)",
                }
              }}
            >
              Sign In
            </Button>
          </Box>
        </Paper>
      </Container>
    </ThemeProvider>
  );
};

export default Login;